export interface Permission {
  id: string;
  label: string;
  feature: string;
}

export interface Role {
  id: string;
  name: string;
  description: string;
  permissions: string[];
  isSystem: boolean;
  createdAt: any;
  updatedAt: any;
  createdBy: string;
}

export interface RoleInfo {
  id: string;
  displayName: string;
}

export interface AdUser {
  microsoftUserId: string;
  displayName: string;
  /** Mail or UPN from Entra. Empty for group principals or when Graph
   *  doesn't return one. Used as a secondary matching key against Firestore
   *  members for cases where OID-based matching misses. */
  email?: string;
  roleId: string | null;
  roleDisplayName: string | null;
  hasSignedIn: boolean;
  firebaseUid?: string;
}

export const ALL_PERMISSIONS: Permission[] = [
  { id: 'workflows.write', label: 'permissions.perm.workflows-write', feature: 'permissions.feature.workflows' },
  { id: 'workflows.delete', label: 'permissions.perm.workflows-delete', feature: 'permissions.feature.workflows' },
  { id: 'members.read', label: 'permissions.perm.members-read', feature: 'permissions.feature.members' },
  { id: 'members.invite', label: 'permissions.perm.members-invite', feature: 'permissions.feature.members' },
  { id: 'members.manage', label: 'permissions.perm.members-manage', feature: 'permissions.feature.members' },
  { id: 'members.remove', label: 'permissions.perm.members-remove', feature: 'permissions.feature.members' },
  { id: 'billing.read', label: 'permissions.perm.billing-read', feature: 'permissions.feature.billing' },
  { id: 'billing.manage', label: 'permissions.perm.billing-manage', feature: 'permissions.feature.billing' },
  { id: 'api-keys.read', label: 'permissions.perm.api-keys-read', feature: 'permissions.feature.api-keys' },
  { id: 'api-keys.manage', label: 'permissions.perm.api-keys-manage', feature: 'permissions.feature.api-keys' },
  { id: 'settings.read', label: 'permissions.perm.settings-read', feature: 'permissions.feature.settings' },
  { id: 'settings.manage', label: 'permissions.perm.settings-manage', feature: 'permissions.feature.settings' },
  { id: 'analytics.read', label: 'permissions.perm.analytics-read', feature: 'permissions.feature.analytics' },
  { id: 'permissions.read', label: 'permissions.perm.permissions-read', feature: 'permissions.feature.permissions' },
  { id: 'permissions.manage', label: 'permissions.perm.permissions-manage', feature: 'permissions.feature.permissions' },
  { id: 'products.read', label: 'permissions.perm.products-read', feature: 'permissions.feature.products' },
  { id: 'products.manage', label: 'permissions.perm.products-manage', feature: 'permissions.feature.products' },
  { id: 'models.read', label: 'permissions.perm.models-read', feature: 'permissions.feature.models' },
  { id: 'models.manage', label: 'permissions.perm.models-manage', feature: 'permissions.feature.models' },
  { id: 'integrations.read', label: 'permissions.perm.integrations-read', feature: 'permissions.feature.integrations' },
  { id: 'integrations.manage', label: 'permissions.perm.integrations-manage', feature: 'permissions.feature.integrations' },
  { id: 'tools.google-maps', label: 'permissions.perm.tools-google-maps', feature: 'permissions.feature.tools' },
  { id: 'tools.google-search', label: 'permissions.perm.tools-google-search', feature: 'permissions.feature.tools' },
  { id: 'tools.web-search', label: 'permissions.perm.tools-web-search', feature: 'permissions.feature.tools' },
  { id: 'tools.perplexity', label: 'permissions.perm.tools-perplexity', feature: 'permissions.feature.tools' },
  { id: 'tools.firecrawl', label: 'permissions.perm.tools-firecrawl', feature: 'permissions.feature.tools' },
  { id: 'tools.browserbase', label: 'permissions.perm.tools-browserbase', feature: 'permissions.feature.tools' },
];

export const PERMISSION_FEATURES = [...new Set(ALL_PERMISSIONS.map(p => p.feature))];

/**
 * Default permission sets for system roles.
 * Used for seeding Firestore and as fallback when role docs don't exist yet.
 */
export const DEFAULT_ADMIN_PERMISSIONS = ['*'];

export const DEFAULT_MEMBER_PERMISSIONS = [
  'workflows.write', 'workflows.delete',
  'members.read', 'billing.read', 'api-keys.read', 'settings.read', 'analytics.read', 'permissions.read',
  'products.read', 'models.read', 'integrations.read',
  'tools.google-maps', 'tools.google-search', 'tools.web-search', 'tools.perplexity', 'tools.firecrawl', 'tools.browserbase',
];

export const SYSTEM_ROLE_DEFINITIONS: { id: string; name: string; description: string; permissions: string[] }[] = [
  { id: 'admin', name: 'Admin', description: 'Administrative access', permissions: DEFAULT_ADMIN_PERMISSIONS },
  { id: 'member', name: 'Member', description: 'Basic member access', permissions: DEFAULT_MEMBER_PERMISSIONS },
];
